import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Optimized Image Component for low bandwidth
const OptimizedImage = ({ src, alt, className }) => {
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`${className} overflow-hidden`}>
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top"
        />
      )}
    </div>
  );
};

// Team data organized by section
const leadershipTeam = [
  {
    id: 'remi',
    name: 'Remi Adebonojo, ICF MCC',
    title: 'Founder and Managing Partner',
    focus: null,
    bio: 'Remi Adebonojo is an International Coaching Federation (ICF) Master Certified Coach (MCC), one of only four MCCs in West Africa, and an EMCC Senior Practitioner, placing him among a highly select group of coaches globally. He brings over 25 years\' experience across financial services, management consulting, risk leadership, and organisational transformation, having held senior roles including Programme Director and Chief Risk Officer. Remi works extensively with boards, executive teams, and senior leaders, and has advised and coached leaders within global banks, regulators, and multinational organisations across Africa, the UK, and Europe. He holds an LLB, an MBA, and an MSc in Change Leadership from HEC Paris and Oxford University (Saïd Business School). He has also been a Fellow of the Chartered Management Institute (UK) for over 15 years.',
    isManagingPartner: true,
    image: '/opt/team/remi-suit.webp'
  },
  {
    id: 'jasmine',
    name: 'Jasmine Victoria',
    title: 'Partner',
    focus: 'Focus: Americas',
    bio: 'Jasmine is a leadership consultant and executive coach with an MSc in Consulting and Coaching for Change (Oxford University & HEC Paris) and executive coaching certification from UC Berkeley. She supports senior leaders and organisations on strategy development and execution, as well as leadership effectiveness through executive coaching and facilitation. Her experience spans professional services, public sector, and complex organisational environments across multiple geographies.',
    image: '/opt/team/jasmine-1920w-rhd42b4a6fyirl18hb9yci9u0p6ubemi42tfkjmhzs.webp'
  },
  {
    id: 'valeriy',
    name: 'Valeriy Pushnya, PhD',
    title: 'Partner',
    focus: 'Focus: Financial Services',
    bio: 'Valeriy is a consultant and researcher with a PhD and strong expertise in analytical thinking, behavioural insight, and evidence-based decision-making. He brings a rigorous academic and data-led perspective to leadership diagnostics, organisational insight, and advisory work, supporting leaders to translate complex data into practical, defensible decisions.',
    image: '/opt/team/valeriy-rhd42tx1z4o97s9xfjehqdj1wem6lcp4unv562umjc.webp'
  },
  {
    id: 'akin',
    name: 'Akin Olunloyo',
    title: 'Partner',
    focus: 'Focus: Change & Transformation',
    bio: 'Akin is a senior change and consulting professional with deep expertise in large-scale organisational, regulatory, and operational transformation, primarily within financial services. He has led complex front-to-back change initiatives, including target operating model design, regulatory remediation, and enterprise transformation programmes. Akin holds an MSc in Change & Consultancy from Birkbeck, University of London, an MSc in Information Technology, and is PRINCE2 Practitioner and ISEB Business Analysis certified.',
    image: '/opt/team/akin-olunloyo.webp'
  },
  {
    id: 'femi',
    name: 'Femi Odelusi, ICF PCC',
    title: 'Partner',
    focus: 'Focus: Business Agility',
    bio: 'Femi brings over 25 years\' experience in leadership, organisational, and transformation coaching across financial services, telecoms, and fintech. His work focuses on agile transformation, leadership adaptability, and navigating complexity at senior levels. He is an ICF Professional Certified Coach (PCC), EMCC Global Senior Practitioner, ORSC™ trained coach, and a Scrum Alliance Certified Agile Coach® (CAC®). Femi is also a Fellow of the Association of Chartered Certified Accountants (ACCA).',
    image: '/opt/team/femi-odelusi-rhd42q5p7sj3xcfe1hrzgeh7iv4pqka7i5978z0788.webp'
  }
];

const consultingTeam = [
  {
    id: 'orojo',
    name: 'Abiola Orojo, ICF MCC',
    title: 'Executive Coach & Trainer',
    bio: 'Abiola Orojo is an experienced executive coach and HR specialist with over 28 years of corporate experience, including 15 years in senior leadership roles. She is an ICF Master Certified Coach (MCC) and one of only four MCCs in West Africa, with an Advanced Team Coaching Certification (ACTC). She holds an MBA, a BA (Hons) in Economics, and is a Fellow of the Institute of Chartered Accountants of Nigeria (FCA). Mrs Orojo has deep expertise in strategic HR, talent and performance management, and has coached C-suite, senior, and middle leaders across Financial Services, Oil & Gas, Public Sector, and International Development.',
    image: '/opt/team/abiola-orojo.webp'
  },
  {
    id: 'franklin',
    name: 'Franklin Ojiego',
    title: 'Strategy & Culture Advisory',
    bio: 'Franklin Ojiego supports RESILIGENT\'s strategy and culture advisory work, working with leadership teams on strategic clarity, execution, and organisational alignment. His work spans culture analysis and development, helping organisations translate strategy into lived behaviours and sustainable performance outcomes.',
    image: '/opt/team/franklin-ojiego.webp'
  },
  {
    id: 'jag',
    name: 'Jag Gill',
    title: 'Leadership Trainer & Coach',
    bio: 'Jag is a leadership trainer and coach with over 20 years\' experience in financial services, specialising in leadership capability, behavioural effectiveness, and people development. He is accredited in DISC personality tools and recognised for creating psychologically safe, high-trust learning environments. Jag was selected for the 50 Human Leaders Cohort (2023) and brings a values-led, practical approach to leadership development.',
    image: '/opt/team/jag-gill-rhd42sz7samyw6bal0zv5vrlb0qtdnleij7nosw0pk.webp'
  },
  {
    id: 'gochi',
    name: 'Ugochi \'Gochi\' Ossai, ICF PCC',
    title: 'Executive Coach',
    bio: 'Gochi is an executive and leadership coach with 17 years\' experience spanning investment banking, capital markets, and leadership development. She previously held senior roles including Managing Associate and COO within financial services. She is an ICF Professional Certified Coach (PCC), a certified group and team coach, and holds an MBA. Gochi works with mid- and senior-level leaders across financial services, insurance, oil & gas, and NGOs, designing coaching interventions that support performance, culture, and retention.',
    image: '/opt/team/gochi-ossai.webp'
  },
  {
    id: 'seyi',
    name: 'Oluwaseyi Adeyemi',
    title: 'HR Specialist & Trainer',
    bio: 'Oluwaseyi Adeyemi is a senior Human Resources professional with over 15 years of experience, including 7 years in senior management roles. She is SPHRi, MCIPM, Ch.HRBP, and FCIHRM certified, with broad expertise across strategic and operational HR. Her experience spans employee relations, training, performance management, recruitment, compensation, HRIS, and benefits administration. Oluwaseyi has led HR audits, organisational restructures, and change initiatives, and provides trusted HR advisory support to senior leadership teams.',
    image: '/opt/team/seyi-adeyemi.webp'
  },
  {
    id: 'yohann',
    name: 'Yohann Orieux',
    title: 'Multilingual Trainer & Coach',
    bio: 'Yohann is a multilingual trainer and coach delivering leadership development and coaching engagements in both English and French. He supports leaders and teams across diverse cultural and organisational contexts, with a strong focus on clarity, engagement, and behavioural impact. Yohann brings cross-cultural fluency and facilitation depth to RESILIGENT\'s international client work.',
    image: '/opt/team/yohann-orieux-rhd42evmxs3o20vrvcwgmhboe8ob671fglfdhngxaw.webp'
  },
  {
    id: 'barbara',
    name: 'Barbara Omowunmi Lawrence, ICF PCC',
    title: 'Executive Coach',
    bio: 'Barbara is an executive and systemic team coach with over 30 years\' experience in leadership development and organisational effectiveness. She spent more than 20 years at Shell, including senior roles across Africa, leading organisational effectiveness and transformation initiatives. She is an ICF Professional Certified Coach (PCC) and EMCC Senior Practitioner, with specialist credentials in systemic team coaching and adult learning. Barbara coaches senior leaders across financial services, oil & gas, public institutions, and international development.',
    image: '/opt/team/barbara2.webp'
  },
  {
    id: 'olayemi',
    name: 'Olayemi Gbadebo-Seriki',
    title: 'Strategy Advisory & Trainer',
    bio: 'Olayemi Gbadebo-Seriki is a seasoned leader and trainer with over 25 years of experience driving strategic growth and operational excellence across multiple sectors. She holds an MBA from the University of Calabar, is a certified Scrum Master, and an associate member of CIPM. Her career includes impactful roles at Resourcery, United Parcel Service, and Diamond Bank, with experience across customer service, business development, performance management, and client engagement. Olayemi is also a committed mentor and board member, with a strong focus on ethical leadership and talent development.',
    image: '/opt/team/yemi-oduala.webp'
  }
];

const operationsTeam = [
  {
    id: 'zainab',
    name: 'Zainab Ado-Braimah',
    title: 'Head of International Country Management',
    bio: 'Zainab brings over 20 years\' experience across commercial leadership, operations, customer experience, and stakeholder management in multinational environments. She oversees country management across RESILIGENT\'s markets, supporting country leads to deliver consistent quality, governance, and client impact. Zainab holds a BSc and MSc in Mass Communication from the University of Lagos and brings strong expertise in market leadership, execution discipline, and client delivery.',
    image: '/opt/team/mrs-zainab-rhd46qwuga19lal4k8cj4dy707b8ny8ve1pz5l1km0.webp'
  },
  {
    id: 'shalom',
    name: 'Shalom Osho',
    title: 'Principal, Business Development & Commercial Strategy',
    bio: 'Shalom Osho leads business development and commercial strategy at RESILIGENT, with responsibility for strategic partnerships, market growth, and the development of commercial opportunities across our markets.',
    image: '/opt/team/image.webp'
  },
  {
    id: 'endeon',
    name: 'Endeon Ekpo',
    title: 'Client Operations Lead',
    bio: 'Endeon Ekpo supports client administration and engagement coordination at RESILIGENT, working with internal teams and clients to ensure smooth communication, scheduling, and operational support across engagements.',
    image: '/opt/team/endeon-ekpo.webp'
  }
];

// Professional placeholder image URLs - neutral portrait style with variety
const placeholderImages = {
  // Leadership - professional male placeholders
  leadershipMale1: '/opt/photo/ext/u1507003211169-0a1dd7228f2d.webp',
  leadershipMale2: '/opt/photo/ext/u1472099645785-5658abf4ff4e.webp',
  leadershipFemale1: '/opt/photo/ext/u1580489944761-15a19d654956.webp',
  // Consulting - professional varied placeholders
  consultingFemale1: '/opt/photo/ext/u1573497019940-1c28c88b4f3e.webp',
  consultingFemale2: '/opt/photo/ext/u1598550874175-4d0ef436c909.webp',
  consultingMale1: '/opt/photo/ext/u1560250097-0b93528c311a.webp',
  consultingMale2: '/opt/photo/ext/u1519085360753-af0119f7cbe7.webp',
  // Operations
  operationsFemale1: '/opt/photo/ext/u1594744803329-e58b31de8bf5.webp',
  operationsFemale2: '/opt/photo/ext/u1573496359142-b8d87734a5a2.webp'
};

// Profile Card Component - Compact Circular Headshot Design
const ProfileCard = ({ person, variant = 'standard', expandedId, setExpandedId }) => {
  const isExpanded = expandedId === person.id;
  const isLeadership = variant === 'leadership';
  const isOperations = variant === 'operations';
  
  const handleToggle = () => {
    setExpandedId(isExpanded ? null : person.id);
  };

  // Get the appropriate image
  const getImage = () => {
    if (person.image) return person.image;
    if (person.imageKey && placeholderImages[person.imageKey]) {
      return placeholderImages[person.imageKey];
    }
    if (isLeadership) return placeholderImages.leadershipMale1;
    if (isOperations) return placeholderImages.operationsFemale1;
    return placeholderImages.consultingFemale1;
  };

  // Headshot size based on variant - balanced sizing (larger on desktop)
  // When expanded, headshots get bigger for leadership/operations
  const headShotSize = isLeadership 
    ? isExpanded ? 'w-24 h-24 lg:w-32 lg:h-32' : 'w-20 h-20 lg:w-24 lg:h-24'
    : isOperations 
      ? isExpanded ? 'w-20 h-20 lg:w-24 lg:h-24' : 'w-16 h-16 lg:w-20 lg:h-20'
      : 'w-16 h-16 lg:w-20 lg:h-20';

  // Make leadership and operations cards clickable
  const isClickableCard = isLeadership || isOperations;

  return (
    <div 
      className={`
        group bg-white transition-all duration-300
        ${isLeadership ? `rounded-lg p-5 lg:p-6 border border-slate-200 hover:border-slate-300 hover:shadow-md animate-float-1 cursor-pointer ${isExpanded ? 'shadow-lg border-[#2693d0]/30 scale-[1.02]' : ''}` : ''}
        ${!isLeadership && !isOperations ? 'rounded-lg p-4 lg:p-5 border border-slate-200 hover:border-slate-300 animate-float-2' : ''}
        ${isOperations ? `px-5 lg:px-6 py-4 lg:py-5 border-b border-slate-100 last:border-b-0 rounded-none cursor-pointer hover:bg-slate-50 ${isExpanded ? 'bg-slate-50 shadow-sm' : ''}` : ''}
      `}
      data-testid={`profile-card-${person.id}`}
      onClick={isClickableCard ? handleToggle : undefined}
    >
      {/* Top section: Circular headshot + Name/Title */}
      <div className="flex items-center gap-4 lg:gap-5">
        {/* Circular Headshot */}
        <div 
          className={`
            ${headShotSize} rounded-full overflow-hidden flex-shrink-0 transition-all duration-300 relative
            ${isExpanded ? 'ring-4 ring-[#2693d0]/20' : 'ring-2 ring-slate-100'}
          `}
        >
          <OptimizedImage
            src={getImage()}
            alt={`${person.name}`}
            className="w-full h-full"
          />
        </div>

        {/* Name, Title, Focus */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-slate-800 leading-snug ${isLeadership ? 'text-[15px] lg:text-[17px]' : isOperations ? 'text-sm lg:text-base' : 'text-sm lg:text-[15px]'}`}>
            {person.name}
          </h3>
          <p className={`text-slate-500 ${isLeadership ? 'text-sm lg:text-base' : isOperations ? 'text-sm lg:text-[15px]' : 'text-xs lg:text-sm'} mt-0.5`}>
            {person.title}
          </p>
          {person.focus && (
            <p className="text-slate-400 text-xs lg:text-sm mt-0.5">
              {person.focus}
            </p>
          )}
        </div>

        {/* Expand indicator for clickable cards */}
        {isClickableCard && (
          <div className={`flex-shrink-0 text-[#2693d0] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown size={20} />
          </div>
        )}

        {/* Inline Bio Toggle - only for non-clickable cards */}
        {!isClickableCard && (
          <button
            onClick={handleToggle}
            className={`flex-shrink-0 text-[#2693d0] font-medium hover:text-[#1e7ab8] transition-colors ${isLeadership ? 'text-sm lg:text-base' : isOperations ? 'text-sm lg:text-base' : 'text-xs lg:text-sm'}`}
            data-testid={`bio-toggle-${person.id}`}
            aria-expanded={isExpanded}
          >
            {isExpanded ? '− Bio' : '+ Bio'}
          </button>
        )}
      </div>

      {/* Expandable Bio */}
      <div 
        className={`
          transition-all duration-300 ease-in-out
          ${isExpanded ? 'opacity-100 mt-4 lg:mt-5' : 'max-h-0 opacity-0 overflow-hidden'}
        `}
      >
        <p className={`text-slate-600 leading-relaxed break-words ${isLeadership ? 'text-sm lg:text-base' : isOperations ? 'text-sm lg:text-[15px]' : 'text-xs lg:text-sm'}`}>
          {person.bio}
        </p>
      </div>
    </div>
  );
};

const OurPeoplePage = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate carousel every 5 seconds (pauses on hover)
  useEffect(() => {
    if (isPaused) return;
    
    const timer = setInterval(() => {
      setCurrentProfileIndex((prev) => (prev + 1) % consultingTeam.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const goToPrevious = () => {
    setCurrentProfileIndex((prev) => 
      prev === 0 ? consultingTeam.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentProfileIndex((prev) => (prev + 1) % consultingTeam.length);
  };

  const currentProfile = consultingTeam[currentProfileIndex];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Our People | ICF Certified Executive Coaches Nigeria & Africa | RESILIGENT"
        description="Meet RESILIGENT's world-class leadership team - ICF MCC and PCC certified executive coaches, organisational development experts, and leadership consultants. Nigeria's largest MCC-led coaching faculty serving Africa, UK, and Europe."
        keywords="ICF MCC coach Nigeria, executive coaches Lagos, leadership consultants Africa, organisational development experts Nigeria, certified coaches West Africa, leadership team Lagos, executive coaching faculty Nigeria"
        canonical="/our-people"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.85) 50%, rgba(10, 10, 10, 0.6) 100%), url('/opt/photo/ext/u1739298061757-7a3339cee982.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 
                className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6"
                data-testid="our-people-h1"
              >
                Our People
              </h1>
              
              <p className="text-slate-300 text-lg lg:text-xl leading-relaxed max-w-2xl">
                RESILIGENT brings together senior practitioners with deep expertise in leadership, coaching, and organisational development across complex environments.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 1: Leadership
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white py-12 lg:py-20" data-testid="leadership-section">
          <div className="w-full max-w-5xl lg:max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-xl sm:text-2xl lg:text-[28px] mb-8 lg:mb-10">
              Leadership
            </h2>
            
            {/* Managing Partner - Featured Row */}
            <div className="mb-6 lg:mb-8">
              <div className="bg-white rounded-lg p-5 lg:p-6 border border-slate-200 shadow-sm animate-float">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 lg:gap-6">
                  {/* Circular Headshot */}
                  <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-slate-100">
                    <img
                      src={leadershipTeam[0].image}
                      alt={leadershipTeam[0].name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-semibold text-slate-800 text-base lg:text-lg">
                      {leadershipTeam[0].name}
                    </h3>
                    <p className="text-[#2693d0] text-sm lg:text-base font-medium mt-0.5">
                      {leadershipTeam[0].title}
                    </p>
                    <p className="text-slate-600 text-sm lg:text-[15px] leading-relaxed mt-3 lg:mt-4">
                      {leadershipTeam[0].bio}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Other Partners - 2 column grid with independent heights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-start">
              {leadershipTeam.slice(1).map((person) => (
                <ProfileCard 
                  key={person.id}
                  person={person} 
                  variant="leadership"
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 2: Featured Profiles - Carousel
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-[#e8f4fa] py-8 sm:py-10 lg:py-16" data-testid="consulting-section">
          <div className="w-full max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-slate-700 font-medium text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10 text-center px-2">
              Featured Profiles - Consulting, Coaching & Training
            </h2>
            
            {/* Carousel Container */}
            <div 
              className="relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Navigation Arrows - Hidden on very small screens */}
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 lg:-translate-x-14 z-10 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#2693d0] hover:border-[#2693d0] transition-all"
                aria-label="Previous profile"
              >
                <ChevronLeft size={18} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 lg:translate-x-14 z-10 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#2693d0] hover:border-[#2693d0] transition-all"
                aria-label="Next profile"
              >
                <ChevronRight size={18} className="sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </button>

              {/* Profile Card - Wider layout for better text flow */}
              <div className={`bg-white rounded-xl p-5 sm:p-6 lg:p-10 shadow-md border border-[#2693d0]/20 max-w-3xl lg:max-w-4xl mx-auto h-[580px] sm:h-[360px] lg:h-[380px] ${isPaused ? '' : 'animate-float'}`}>
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 lg:gap-8 h-full">
                  {/* Circular Headshot - Larger for carousel */}
                  <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden flex-shrink-0 ring-4 ring-slate-100">
                    <img
                      src={currentProfile.image || placeholderImages.consultingFemale1}
                      alt={currentProfile.name}
                      className="w-full h-full object-cover object-top transition-opacity duration-500"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <h3 className="font-semibold text-slate-800 text-base sm:text-lg lg:text-xl break-words">
                      {currentProfile.name}
                    </h3>
                    <p className="text-[#2693d0] text-sm lg:text-base font-medium mt-1 break-words">
                      {currentProfile.title}
                    </p>
                    <p className="text-slate-600 text-xs sm:text-sm lg:text-base leading-relaxed mt-3 sm:mt-4 lg:mt-5 break-words">
                      {currentProfile.bio}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 lg:gap-3 mt-5 sm:mt-6 lg:mt-8">
                {consultingTeam.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProfileIndex(index)}
                    className={`w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full transition-all duration-300 ${
                      index === currentProfileIndex 
                        ? 'bg-[#2693d0] w-6 lg:w-8' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    aria-label={`Go to profile ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 3: Commercial, Operations & Client Engagement
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white py-10 lg:py-14" data-testid="operations-section">
          <div className="w-full max-w-5xl lg:max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-600 font-medium text-base lg:text-lg mb-5 lg:mb-6">
              Commercial, Operations & Client Engagement
            </h2>
            
            {/* Operations - List style */}
            <div className="bg-white border border-slate-100 rounded-lg overflow-hidden">
              {operationsTeam.map((person) => (
                <ProfileCard 
                  key={person.id}
                  person={person} 
                  variant="operations"
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Blue Divider Line */}
        <div className="w-full max-w-5xl lg:max-w-6xl mx-auto px-6 lg:px-8">
          <div className="h-px bg-gradient-to-r from-transparent via-[#2693d0] to-transparent" />
        </div>

        {/* CERTIFICATIONS & ACCREDITATIONS */}
        <section className="bg-white py-12 lg:py-16 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-slate-500 text-xs uppercase tracking-[0.2em] text-center mb-8 sm:mb-10">
              Certifications & Accreditations
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10" aria-hidden="true" />
            <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10" aria-hidden="true" />
            
            <div className="certifications-track">
              {/* First set */}
              {[
                { src: "/opt/accreditations/icf-mcc.webp", alt: "ICF MCC" },
                { src: "/logos/accreditations/emcc-member.png", alt: "EMCC Member" },
                { src: "/logos/accreditations/emcc-eia.png", alt: "EMCC EIA Accreditation" },
                { src: "/opt/accreditations/hec-paris.webp", alt: "HEC Paris" },
                { src: "/opt/accreditations/oxford-said.webp", alt: "Oxford Saïd Business School" },
                { src: "/logos/accreditations/hogan.png", alt: "Hogan Certified" },
                { src: "/logos/accreditations/forbes.png", alt: "Forbes Coaches Council" },
                { src: "/opt/accreditations/lcp.webp", alt: "Leadership Circle Profile", larger: true },
                { src: "/logos/accreditations/five-behaviours.png", alt: "Five Behaviours" },
                { src: "/logos/accreditations/orsc.png", alt: "ORSC Certified" },
                { src: "/logos/accreditations/scrum.png", alt: "Scrum Certified" },
                { src: "/logos/accreditations/cmi.png", alt: "CMI" },
              ].map((logo, index) => (
                <div key={`a-${index}`} className="cert-item">
                  <img 
                    src={logo.src} 
                    alt={logo.alt}
                    loading="lazy"
                    className={`w-auto max-w-[140px] sm:max-w-[180px] object-contain ${logo.larger ? 'h-14 sm:h-20 lg:h-24' : 'h-10 sm:h-14 lg:h-16'}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {[
                { src: "/opt/accreditations/icf-mcc.webp", alt: "ICF MCC" },
                { src: "/logos/accreditations/emcc-member.png", alt: "EMCC Member" },
                { src: "/logos/accreditations/emcc-eia.png", alt: "EMCC EIA Accreditation" },
                { src: "/opt/accreditations/hec-paris.webp", alt: "HEC Paris" },
                { src: "/opt/accreditations/oxford-said.webp", alt: "Oxford Saïd Business School" },
                { src: "/logos/accreditations/hogan.png", alt: "Hogan Certified" },
                { src: "/logos/accreditations/forbes.png", alt: "Forbes Coaches Council" },
                { src: "/opt/accreditations/lcp.webp", alt: "Leadership Circle Profile", larger: true },
                { src: "/logos/accreditations/five-behaviours.png", alt: "Five Behaviours" },
                { src: "/logos/accreditations/orsc.png", alt: "ORSC Certified" },
                { src: "/logos/accreditations/scrum.png", alt: "Scrum Certified" },
                { src: "/logos/accreditations/cmi.png", alt: "CMI" },
              ].map((logo, index) => (
                <div key={`b-${index}`} className="cert-item">
                  <img 
                    src={logo.src} 
                    alt={logo.alt}
                    loading="lazy"
                    className={`w-auto max-w-[140px] sm:max-w-[180px] object-contain ${logo.larger ? 'h-14 sm:h-20 lg:h-24' : 'h-10 sm:h-14 lg:h-16'}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Blue Background like Featured Profiles */}
        <section className="bg-[#e8f4f8] py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-4">
              Ready to strengthen your leadership?
            </h2>
            <p className="text-slate-600 text-base lg:text-lg mb-8 max-w-2xl mx-auto">
              Connect with our team to discuss how we can support your organisation's leadership journey.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 bg-[#2693d0] text-white font-medium text-base lg:text-lg rounded-lg hover:bg-[#1e7ab8] transition-colors duration-300 shadow-lg hover:shadow-xl"
            >
              Get in Touch
            </a>
          </div>
        </section>
          
        <style>{`
          .certifications-track {
            display: flex;
            width: calc(200%);
            animation: scroll-certs 40s linear infinite;
          }
          .cert-item {
            position: relative;
            flex: 0 0 auto;
            padding: 0.5rem 2.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 180px;
          }
          @keyframes scroll-certs {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .certifications-track:hover {
            animation-play-state: paused;
          }
          @media (prefers-reduced-motion: reduce) {
            .certifications-track {
              animation: none;
            }
          }
        `}</style>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default OurPeoplePage;
